import {
  Crown,
  type LucideIcon,
  Shield,
  Sparkles,
  Users,
  Zap,
} from 'lucide-react';

export interface BillingSuccessTierPresentation {
  name: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  features: string[];
}

const BILLING_SUCCESS_TIER_PRESENTATIONS: Record<string, BillingSuccessTierPresentation> = {
  free: {
    name: 'Free',
    icon: Sparkles,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    features: [
      '1 memory space',
      'Unlimited memories',
      'Text-only workspace',
    ],
  },
  starter: {
    name: 'Starter',
    icon: Zap,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    features: [
      '3 memory spaces',
      'Unlimited memories',
      '25,000 API calls/month',
      'Voice memories, transcribed and searchable (10 minutes per file)',
    ],
  },
  pro: {
    name: 'Pro',
    icon: Crown,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    features: [
      'Unlimited spaces and memories',
      '250,000 API calls/month',
      'Voice memories, transcribed and searchable (1 hour per file)',
      'Priority support',
    ],
  },
  enterprise: {
    name: 'Enterprise',
    icon: Shield,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    features: [
      'Unlimited scale with custom limits',
      'SSO and enterprise controls',
      'Dedicated support',
      'Voice memories, transcribed and searchable (2 hours per file)',
    ],
  },
  team: {
    name: 'Team',
    icon: Users,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
    features: [
      'Legacy team subscription',
      'Shared workspace collaboration',
      'High API allowances',
      'Priority support',
    ],
  },
};

const toTitleCase = (value: string): string =>
  value
    .split(/[_-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

export const getBillingSuccessTierPresentation = (tier: string): BillingSuccessTierPresentation => {
  const normalizedTier = (tier || 'free').toLowerCase();
  const knownPresentation = BILLING_SUCCESS_TIER_PRESENTATIONS[normalizedTier];

  if (knownPresentation) {
    return knownPresentation;
  }

  return {
    name: toTitleCase(normalizedTier) || 'Plan',
    icon: Sparkles,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    features: ['Your plan is now active'],
  };
};
